import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { PageHeader } from "../../components/StaffLayout";
import { Modal, Inp, Sel } from "./Enquiries";
import { Plus, Pencil, ShieldCheck, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { MODULES } from "../../context/AuthContext";

const MODULE_LABELS = {
  dashboard: "Dashboard",
  calendar: "Calendar",
  enquiries: "Enquiries",
  patients: "Patients",
  appointments: "Appointments",
  reports: "Reports",
  treatments: "Treatments",
  invoices: "Invoices",
  reminders: "Reminders",
  templates: "Plan Templates",
  doctors: "Doctors",
  settings: "Settings",
  staff_roles: "Staff Roles",
};

const DEFAULT_STAFF_MODULES = MODULES.filter((module) =>
  !["templates", "doctors", "settings", "staff_roles"].includes(module)
);

export default function StaffRoles() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get("/staff-users")).data);

  useEffect(() => {
    load().catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
  }, []);

  return (
    <div data-testid="staff-roles-page">
      <PageHeader
        title="Staff Role Management"
        subtitle={`${items.length} admin/staff account${items.length === 1 ? "" : "s"} with CRM access`}
        actions={
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm"
          >
            <Plus size={16} weight="bold" /> Add staff account
          </button>
        }
      />

      <div className="px-6 sm:px-10 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((item) => (
            <div key={item.id} className="bg-white border border-zinc-200 rounded-sm p-5" data-testid={`staff-role-${item.id}`}>
              <div className="flex items-start gap-3">
                <div className={`rounded-sm w-12 h-12 flex items-center justify-center text-white ${item.role === "admin" ? "bg-zinc-900" : "bg-[#E60000]"}`}>
                  <ShieldCheck size={22} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-zinc-900">{item.name}</div>
                  <div className="text-xs text-zinc-500 mt-1">{item.email}</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-600">
                    {item.role}
                  </div>
                </div>
                <button onClick={() => setEditing(item)} className="text-zinc-500 hover:text-[#E60000]">
                  <Pencil size={16} weight="bold" />
                </button>
              </div>
              <div className="mt-4 text-xs text-zinc-500">Phone: {item.phone || "-"}</div>
              <div className="text-xs text-zinc-500 mt-1">Created: {item.created_at ? new Date(item.created_at).toLocaleString() : "-"}</div>
              {item.role === "staff" ? (
                <div className="text-xs text-zinc-500 mt-1">{(item.modules || []).length} module{(item.modules || []).length === 1 ? "" : "s"} enabled</div>
              ) : null}
            </div>
          ))}
          {items.length === 0 ? <div className="text-sm text-zinc-500 col-span-full text-center py-8">No staff accounts yet.</div> : null}
        </div>
      </div>

      {showNew ? (
        <StaffUserModal
          onClose={() => setShowNew(false)}
          onSaved={() => {
            setShowNew(false);
            load();
          }}
        />
      ) : null}

      {editing ? (
        <StaffUserModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      ) : null}
    </div>
  );
}

function StaffUserModal({ item, onClose, onSaved }) {
  const isEdit = Boolean(item);
  const [form, setForm] = useState({
    name: item?.name || "",
    email: item?.email || "",
    role: item?.role || "staff",
    phone: item?.phone || "",
    password: "",
    modules: item?.modules || DEFAULT_STAFF_MODULES,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        phone: form.phone || null,
      };

      if (form.role === "staff") {
        payload.modules = form.modules;
      }

      if (!isEdit || form.password.trim() !== "") {
        payload.password = form.password;
      }

      if (isEdit) {
        await api.patch(`/staff-users/${item.id}`, payload);
      } else {
        await api.post("/staff-users", payload);
      }
      toast.success(isEdit ? "Staff account updated" : "Staff account created");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this staff account?")) return;
    try {
      await api.delete(`/staff-users/${item.id}`);
      toast.success("Staff account removed");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Modal onClose={onClose} title={isEdit ? "Edit staff account" : "Add staff account"}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Inp label="Full name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="staff-name" />
        <Inp label="Email" v={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="staff-email" />
        <Sel
          label="Role"
          v={form.role}
          onChange={(v) => setForm({ ...form, role: v })}
          opts={[
            ["staff", "Staff"],
            ["admin", "Admin"],
          ]}
          testid="staff-role"
        />
        <Inp label="Phone" v={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="staff-phone" />
        <div className="sm:col-span-2">
          <Inp
            label={isEdit ? "New password (optional)" : "Password"}
            type="password"
            v={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
            testid="staff-password"
          />
          {isEdit ? <div className="text-xs text-zinc-500 mt-2">Leave blank to keep the current password.</div> : null}
        </div>
        <div className="sm:col-span-2 border border-zinc-200 rounded-sm p-3">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Module access</div>
          {form.role === "admin" ? (
            <div className="text-xs text-zinc-500 mt-2">Admins always have full module access.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
              {MODULES.filter((module) => module !== "staff_roles").map((module) => (
                <label key={module} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.modules.includes(module)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((current) => ({
                        ...current,
                        modules: checked
                          ? [...current.modules, module]
                          : current.modules.filter((value) => value !== module),
                      }));
                    }}
                  />
                  <span>{MODULE_LABELS[module] || module}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between">
        {isEdit ? (
          <button onClick={remove} className="text-xs uppercase tracking-[0.2em] font-bold text-[#E60000] inline-flex items-center gap-1">
            <Trash size={14} weight="bold" /> Remove
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <button onClick={onClose} className="bg-transparent border border-zinc-900 text-zinc-900 font-semibold rounded-sm px-4 py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm disabled:opacity-60"
          >
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
